/* SuburbAreaCodesAdminPage — Iter211
 *
 * Lets office admins maintain the suburb ↔ city ↔ scheduling-area_code
 * mapping that drives the City→Suburb cascade on the New Client form
 * AND the day-routing "Scheduling Area" badge.
 *
 * Backed by the existing `address_suburbs_meta` dropdown_options doc.
 * Each row is `{ name, city, area_code }`. Saving rewrites the whole
 * options array via PUT /api/dropdowns/address_suburbs_meta — the
 * backend cascades the rewrite into the flat `address_suburbs` list
 * so the cascade picker stays consistent.
 *
 * Why a dedicated page (not the generic Dropdown Manager):
 *   - Each entry is a triple, not a string.
 *   - Operators want to see suburbs grouped by city.
 *   - Area Code is a tiny single-letter input that benefits from inline
 *     edit + immediate save UX (no modal pop-up overhead).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Save, Trash2, Plus, MapPin } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function SuburbAreaCodesAdminPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);            // [{name, city, area_code}]
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterCity, setFilterCity] = useState('');
  const [filterText, setFilterText] = useState('');
  const [dirtyKeys, setDirtyKeys] = useState(new Set());
  // New-row form state
  const [newRow, setNewRow] = useState({ name: '', city: '', area_code: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: meta }, { data: cityList }] = await Promise.all([
        api.get('/dropdowns/address_suburbs_meta'),
        api.get('/dropdowns/address_cities'),
      ]);
      const normalised = (Array.isArray(meta) ? meta : []).map(m => ({
        name: (m?.name || '').trim(),
        city: (m?.city || '').trim(),
        area_code: (m?.area_code || '').trim(),
      })).filter(r => r.name);
      // Stable sort: city then name
      normalised.sort((a, b) => (a.city || '').localeCompare(b.city || '') || a.name.localeCompare(b.name));
      setRows(normalised);
      setCities(Array.isArray(cityList) ? cityList : []);
      setDirtyKeys(new Set());
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const keyOf = (r) => `${r.name}|${r.city}`.toLowerCase();

  const filtered = useMemo(() => {
    const q = (filterText || '').trim().toLowerCase();
    return rows.filter(r => {
      if (filterCity && r.city !== filterCity) return false;
      if (q && !r.name.toLowerCase().includes(q) && !r.city.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, filterCity, filterText]);

  // Group filtered rows by city for a tidy display
  const groupedByCity = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const key = r.city || '(no city)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const updateRow = (idx, patch) => {
    setRows(prev => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
    setDirtyKeys(prev => {
      const k = keyOf(rows[idx]);
      const n = new Set(prev);
      n.add(k);
      return n;
    });
  };

  const removeRow = (idx) => {
    const r = rows[idx];
    if (!window.confirm(`Remove "${r.name}" (${r.city || 'no city'}) from the suburb list?`)) return;
    setRows(prev => prev.filter((_, i) => i !== idx));
    setDirtyKeys(prev => {
      const n = new Set(prev);
      n.add(keyOf(r));
      return n;
    });
  };

  const addRow = () => {
    const name = (newRow.name || '').trim();
    const city = (newRow.city || '').trim();
    const area_code = (newRow.area_code || '').trim().toUpperCase();
    if (!name) { toast.error('Suburb name required'); return; }
    if (!city) { toast.error('Pick a city'); return; }
    const exists = rows.some(r => r.name.toLowerCase() === name.toLowerCase() && r.city.toLowerCase() === city.toLowerCase());
    if (exists) { toast.error(`"${name}" already exists under ${city}`); return; }
    setRows(prev => {
      const next = [...prev, { name, city, area_code }];
      next.sort((a, b) => (a.city || '').localeCompare(b.city || '') || a.name.localeCompare(b.name));
      return next;
    });
    setDirtyKeys(prev => {
      const n = new Set(prev);
      n.add(`${name}|${city}`.toLowerCase());
      return n;
    });
    setNewRow({ name: '', city, area_code: '' });
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      // De-dupe defensively and persist
      const clean = rows
        .map(r => ({
          name: (r.name || '').trim(),
          city: (r.city || '').trim(),
          area_code: (r.area_code || '').trim().toUpperCase(),
        }))
        .filter(r => r.name);
      await api.put('/dropdowns/address_suburbs_meta', { options: clean });
      toast.success(`Saved ${clean.length} suburb${clean.length === 1 ? '' : 's'}.`);
      setDirtyKeys(new Set());
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const cityOptionsForNew = cities.length ? cities : [...new Set(rows.map(r => r.city).filter(Boolean))].sort();
  const dirtyCount = dirtyKeys.size;

  return (
    <div data-testid="suburb-area-codes-admin-page" className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope] flex items-center gap-2">
            <MapPin size={24} className="text-[#D35400]" /> Suburb Area Codes
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Maintain the suburb ↔ city ↔ scheduling area code (A–H) mapping that drives the New Client form's City → Suburb cascade and the day-routing badge. Area codes are RTR-specific and most useful for Hamilton; other towns can leave the code blank.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="suburb-admin-reload"
            onClick={load}
            disabled={loading || saving}
            className="flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Reload
          </button>
          <button
            data-testid="suburb-admin-save-all"
            onClick={saveAll}
            disabled={saving || dirtyCount === 0}
            className="flex items-center gap-2 px-3 py-2 bg-[#D35400] hover:bg-[#B84A00] text-white rounded-md text-sm font-bold disabled:opacity-40"
            title={dirtyCount === 0 ? 'No unsaved changes' : `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}`}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
          </button>
        </div>
      </div>

      {/* Add new suburb */}
      <div className="bg-white border border-slate-200 rounded-md p-3">
        <h2 className="text-xs uppercase tracking-[0.12em] font-bold text-slate-700 mb-2">Add new suburb</h2>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[180px]">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Suburb name</span>
            <input
              data-testid="suburb-admin-new-name"
              type="text"
              value={newRow.name}
              onChange={e => setNewRow(s => ({ ...s, name: e.target.value }))}
              placeholder="e.g. Leamington"
              className="mt-0.5 w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-[#D35400] focus:border-[#D35400]"
            />
          </label>
          <label className="flex-1 min-w-[160px]">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">City</span>
            <input
              data-testid="suburb-admin-new-city"
              type="text"
              list="suburb-admin-cities"
              value={newRow.city}
              onChange={e => setNewRow(s => ({ ...s, city: e.target.value }))}
              placeholder="e.g. Cambridge"
              className="mt-0.5 w-full px-2 py-1.5 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-[#D35400] focus:border-[#D35400]"
            />
            <datalist id="suburb-admin-cities">
              {cityOptionsForNew.map(c => <option key={c} value={c} />)}
            </datalist>
          </label>
          <label className="w-24">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Area code</span>
            <input
              data-testid="suburb-admin-new-area"
              type="text"
              maxLength={3}
              value={newRow.area_code}
              onChange={e => setNewRow(s => ({ ...s, area_code: e.target.value.toUpperCase() }))}
              placeholder="A–H"
              className="mt-0.5 w-full px-2 py-1.5 text-sm font-mono uppercase border border-slate-300 rounded focus:ring-2 focus:ring-[#D35400] focus:border-[#D35400] text-center"
            />
          </label>
          <button
            data-testid="suburb-admin-new-add"
            onClick={addRow}
            className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-bold"
          >
            <Plus size={14} /> Add
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Adds are staged in this list — click <span className="font-bold">Save</span> at the top right to persist.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
        <span className="text-xs uppercase tracking-[0.12em] font-bold text-slate-600">Filter:</span>
        <select
          data-testid="suburb-admin-filter-city"
          value={filterCity}
          onChange={e => setFilterCity(e.target.value)}
          className="px-2 py-1 text-sm border border-slate-300 rounded bg-white"
        >
          <option value="">All cities</option>
          {[...new Set(rows.map(r => r.city).filter(Boolean))].sort().map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <input
          data-testid="suburb-admin-filter-text"
          type="text"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          placeholder="Search suburb or city…"
          className="flex-1 min-w-[180px] px-2 py-1 text-sm border border-slate-300 rounded bg-white"
        />
        <span className="text-xs text-slate-500 ml-auto">
          Showing {filtered.length} of {rows.length} · {dirtyCount} unsaved
        </span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : groupedByCity.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No suburbs match the current filter.</div>
        ) : (
          groupedByCity.map(([city, group]) => (
            <div key={city} className="border-b border-slate-100 last:border-b-0">
              <div className="bg-slate-50 px-3 py-1.5 text-xs uppercase tracking-[0.12em] font-bold text-slate-700 sticky top-0">
                {city} <span className="text-slate-400 font-normal normal-case ml-1">· {group.length} suburb{group.length === 1 ? '' : 's'}</span>
              </div>
              <div className="divide-y divide-slate-100">
                {group.map((r) => {
                  const idx = rows.indexOf(r);
                  const isDirty = dirtyKeys.has(keyOf(r));
                  return (
                    <div
                      key={`${r.city}|${r.name}|${idx}`}
                      data-testid={`suburb-row-${(r.name || '').toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 ${isDirty ? 'bg-amber-50' : ''}`}
                    >
                      <input
                        type="text"
                        value={r.name}
                        onChange={e => updateRow(idx, { name: e.target.value })}
                        className="flex-1 px-2 py-1 text-sm border border-transparent hover:border-slate-200 focus:border-[#D35400] focus:bg-white rounded"
                      />
                      <input
                        type="text"
                        value={r.city}
                        onChange={e => updateRow(idx, { city: e.target.value })}
                        list="suburb-admin-cities"
                        className="w-48 px-2 py-1 text-sm border border-transparent hover:border-slate-200 focus:border-[#D35400] focus:bg-white rounded"
                      />
                      <input
                        data-testid={`suburb-area-code-${(r.name || '').toLowerCase().replace(/\s+/g, '-')}`}
                        type="text"
                        maxLength={3}
                        value={r.area_code}
                        onChange={e => updateRow(idx, { area_code: e.target.value.toUpperCase() })}
                        placeholder="—"
                        className="w-16 px-2 py-1 text-sm font-mono uppercase text-center border border-slate-300 focus:border-[#D35400] focus:bg-white rounded"
                      />
                      <button
                        onClick={() => removeRow(idx)}
                        title="Delete this suburb"
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <p className="text-[11px] text-slate-400 italic">
        Note — the City list comes from <span className="font-mono">address_cities</span>. To add an entirely new city, head to <span className="font-mono">/dropdown-manager</span>, add it under <span className="font-mono">address_cities</span>, then return here to attach suburbs.
      </p>
    </div>
  );
}
